import React from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import { columnDefs, defaultColDef } from './definitions-config';

import {
  getAlmaXAssignmentList,
  updateAlmaXAssignmentStatus,
  assignAlmaXEvaluatorInProject,
  getEvaluatorListForProjectSubmission,
  getAllAlmaXAssignmentList,
} from '../../../services/apiService';

const StatusDropdownContext = React.createContext();
const EvaluatorDropdownContext = React.createContext();

function AlmaXSubmissionTable({
  goalType,
  refreshCounter,
  isApplyDefaultFilter,
  setIsApplyDefaultFilter,
}) {
  const [gridApi, setGridApi] = React.useState(null);

  const pageSize = 10;

  const [evaluatorList, setEvaluatorList] = React.useState([]);

  const [allAssignmentList, setAllAssignmentList] = React.useState([]);

  const getAllAssignmentList = (selectedList, dataList) => {
    if (selectedList && dataList && dataList.length) {
      const res = selectedList.map((item) => {
        const index = dataList.findIndex((itm) => itm.name === item);
        if (index < 0) return null;
        return dataList[index].id;
      });
      if (res.indexOf(null) >= 0) return null;
      return res;
    }
    return null;
  };

  const getServerSideDatasource = (mGoalType, mAllAssignmentList) => {
    return {
      getRows: (params) => {
        const sortFn = () => {
          if (params.request?.sortModel?.length > 0) {
            return {
              [params.request?.sortModel[0]?.colId]:
                params.request?.sortModel[0]?.sort.toUpperCase(),
            };
          }

          return { submittedAt: 'DESC' };
        };

        const getDateFilterObj = (dateFilter) => {
          if (!dateFilter) return null;
          if (dateFilter.type === 'inRange') {
            const dateFrom = dateFilter.dateFrom.split(' ')[0];
            const dateTo = dateFilter.dateTo.split(' ')[0];
            const sign = '$between';
            return {
              [sign]: { startDate: dateFrom, endDate: dateTo },
            };
          }
          const date = dateFilter.dateFrom.split(' ')[0];
          let sign = '';
          if (dateFilter.type === 'equals') sign = '$eq';
          else if (dateFilter.type === 'greaterThan') sign = '$gt';

          return {
            [sign]: date,
          };
        };

        const body = {
          filter: {
            firstName: params.request?.filterModel?.name?.filter || null,
            adminStatus: params.request?.filterModel?.adminStatus?.values || [
              'pending',
              'on_hold',
              'accepted',
              'not_needed',
            ],
            goalType: mGoalType,
            email: params.request?.filterModel?.email?.filter || null,
            cohort: params.request?.filterModel?.cohort?.filter || null,
            almaxCohortName:
              params.request?.filterModel?.almaXCohort?.filter || null,
            evaluatorName:
              params.request?.filterModel?.evaluator?.type === 'contains'
                ? params.request?.filterModel?.evaluator?.filter || null
                : null,
            evaluatorEmail:
              params.request?.filterModel?.evaluatorEmail?.filter || null,
            emptyEvaluator:
              params.request?.filterModel?.evaluator?.type === 'noEvaluator' || null,

            submittedAt: getDateFilterObj(
              params.request?.filterModel?.submittedAt
            ),
            evaluatedAt: getDateFilterObj(
              params.request?.filterModel?.evaluatedAt
            ),

            evaluationStatus: params.request?.filterModel?.evaluationStatus
              ?.values || ['pending', 'completed', 'rejected'],
            assignmentIds: getAllAssignmentList(
              params.request?.filterModel?.assignment?.values,
              mAllAssignmentList
            ),
          },
          sort: sortFn(),
          pagination: {
            start: params.request.startRow,
            end: params.request.endRow,
          },
        };

        try {
          getAlmaXAssignmentList(body).then((response) => {
            params.successCallback(
              response?.data || [],
              response?.pagination?.totalCount || 0
            );
          });
        } catch (error) {
          console.error(error);
          params.failCallback();
        }
      },
    };
  };

  const statusDropdownParams = {
    statusChangeFn: async (submissionId, status, adminFeedback) => {
      const body = {
        submissionId: submissionId,
        adminStatus: status,
        adminFeedback,
        goalType,
      };

      try {
        await updateAlmaXAssignmentStatus(body);
        reloadData();
      } catch (error) {
        console.error(error);
      }
    },
  };

  const evaluatorDropdownParams = {
    evaluatorList: evaluatorList,
    evaluatorChangeFn: async (submissionId, evaluatorUserId) => {
      const body = {
        submissionId,
        evaluatorUserId,
      };

      try {
        await assignAlmaXEvaluatorInProject(body);
        reloadData();
      } catch (e) {
        console.error(e);
      }
    },
  };

  const reloadData = () => {
    if (gridApi) {
      onGridReady(gridApi, goalType, isApplyDefaultFilter, allAssignmentList);
    }
  };

  React.useEffect(async () => {
    const mAllAlmaXAssignmentResponse = await getAllAlmaXAssignmentList(
      goalType
    );
    setAllAssignmentList(mAllAlmaXAssignmentResponse);
  }, []);

  React.useEffect(() => {
    reloadData();
  }, [goalType, refreshCounter, allAssignmentList]);

  React.useEffect(() => {
    if (isApplyDefaultFilter) reloadData();
  }, [isApplyDefaultFilter]);

  // get evaluator list
  React.useEffect(async () => {
    try {
      const response = await getEvaluatorListForProjectSubmission();
      setEvaluatorList(response);
    } catch (e) {
      console.error(e);
    }
  }, []);

  React.useEffect(async () => {
    try {
      if (gridApi) {
        const mAllAlmaXAssignmentResponse = await getAllAlmaXAssignmentList(
          goalType
        );
        setAllAssignmentList(mAllAlmaXAssignmentResponse);
        gridApi.api
          .getFilterInstance('assignment')
          .setFilterValues(
            mAllAlmaXAssignmentResponse?.map((project) => project.name)
          );
      }
    } catch (err) {
      console.error(err);
    }
  }, [goalType]);

  const onGridReady = React.useCallback(
    (
      params,
      mGoalType = 'dataScience',
      mIsApplyDefaultFilter = true,
      mAllAssignmentList
    ) => {
      setGridApi(params);

      if (mIsApplyDefaultFilter) {
        // const defaultFilter = {
        //   adminStatus: { values: ['pending'], filterType: 'set' },
        // };
        params.api.setFilterModel({});
        setIsApplyDefaultFilter(false);
      }

      const datasource = getServerSideDatasource(mGoalType, mAllAssignmentList);
      params.api.setServerSideDatasource(datasource);
    },
    []
  );

  return (
    <div className='ag-theme-alpine z-50' style={{ width: '100%' }}>
      <EvaluatorDropdownContext.Provider value={evaluatorDropdownParams}>
        <StatusDropdownContext.Provider value={statusDropdownParams}>
          <AgGridReact
            columnDefs={columnDefs(allAssignmentList)}
            defaultColDef={defaultColDef}
            tooltipShowDelay={0}
            tooltipHideDelay={2000}
            onGridReady={onGridReady}
            domLayout='autoHeight'
            rowModelType='serverSide'
            rowHeight={60}
            serverSideStoreType='partial'
            animateRows={true}
            serverSideInfiniteScroll={true}
            pagination={true}
            suppressPaginationPanel={false}
            paginationPageSize={pageSize}
            cacheBlockSize={pageSize}
            // enableCellTextSelection={true}
          />
        </StatusDropdownContext.Provider>
      </EvaluatorDropdownContext.Provider>
    </div>
  );
}

export default AlmaXSubmissionTable;
export { StatusDropdownContext };
export { EvaluatorDropdownContext };
